import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Calculator as CalcIcon, X } from "@phosphor-icons/react";

/* A simple iOS-style calculator. The header button shows a 3D-style gradient
   chip; clicking opens a centered modal (always dark, like the iOS app). */
export function CalculatorButton() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button onClick={() => setOpen(true)} title="Calculator" data-testid="calc-open" aria-label="Open calculator"
        className="grid h-9 w-9 place-items-center rounded-xl bg-gradient-to-br from-neon-cyan to-neon-lime text-black shadow-[0_4px_14px_rgba(0,229,255,0.35)] transition active:scale-95">
        <CalcIcon size={18} weight="fill" />
      </button>
      <AnimatePresence>{open && <CalculatorModal onClose={() => setOpen(false)} />}</AnimatePresence>
    </>
  );
}

const fmt = (n) => {
  if (!isFinite(n)) return "Error";
  const r = Math.round((n + Number.EPSILON) * 1e10) / 1e10;
  return String(r).length > 12 ? r.toPrecision(9).replace(/\.?0+$/, "") : String(r);
};

export function CalculatorModal({ onClose }) {
  const [display, setDisplay] = useState("0");
  const [prev, setPrev] = useState(null);
  const [op, setOp] = useState(null);
  const [fresh, setFresh] = useState(true);

  const compute = (a, b, o) => (o === "+" ? a + b : o === "−" ? a - b : o === "×" ? a * b : o === "÷" ? (b === 0 ? NaN : a / b) : b);
  const digit = useCallback((d) => { setDisplay((c) => (fresh || c === "0" ? d : c + d)); setFresh(false); }, [fresh]);
  const dot = useCallback(() => { if (fresh) { setDisplay("0."); setFresh(false); } else setDisplay((c) => (c.includes(".") ? c : c + ".")); }, [fresh]);
  const clear = useCallback(() => { setDisplay("0"); setPrev(null); setOp(null); setFresh(true); }, []);
  const back = useCallback(() => { if (fresh) return; setDisplay((c) => (c.length > 1 ? c.slice(0, -1) : (setFresh(true), "0"))); }, [fresh]);
  const pct = useCallback(() => { setDisplay((c) => fmt(parseFloat(c) / 100)); setFresh(true); }, []);
  const neg = useCallback(() => setDisplay((c) => (c === "0" ? c : fmt(parseFloat(c) * -1))), []);
  const chooseOp = useCallback((next) => {
    const c = parseFloat(display);
    setPrev((p) => { if (p === null) return c; if (op && !fresh) { const r = compute(p, c, op); setDisplay(fmt(r)); return r; } return p; });
    setOp(next); setFresh(true);
  }, [display, op, fresh]);
  const equals = useCallback(() => { if (op === null || prev === null) return; const r = compute(prev, parseFloat(display), op); setDisplay(fmt(r)); setPrev(null); setOp(null); setFresh(true); }, [op, prev, display]);

  useEffect(() => {
    const h = (e) => {
      const k = e.key;
      if (k >= "0" && k <= "9") digit(k);
      else if (k === ".") dot();
      else if (k === "+") chooseOp("+");
      else if (k === "-") chooseOp("−");
      else if (k === "*") chooseOp("×");
      else if (k === "/") { e.preventDefault(); chooseOp("÷"); }
      else if (k === "Enter" || k === "=") { e.preventDefault(); equals(); }
      else if (k === "Backspace") back();
      else if (k === "Escape") onClose();
      else if (k === "%") pct();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [digit, dot, chooseOp, equals, back, pct, onClose]);

  // iOS palette: util keys grey, operators orange, digits dark grey.
  const Key = ({ label, onClick, kind = "num", testid }) => {
    const styles = {
      num: "bg-[#333333] text-white active:bg-[#444]",
      util: "bg-[#6e6e6e] text-white active:bg-[#7d7d7d]",
      op: "bg-[#ff9500] text-white text-[30px] active:bg-[#ffac3b]",
    };
    return (
      <button onClick={onClick} data-testid={testid || `calc-${label}`}
        className={`flex aspect-square items-center justify-center rounded-full text-[26px] font-medium transition ${styles[kind]}`}>
        {label}
      </button>
    );
  };

  // Rendered at the AppShell ROOT (outside the backdrop-blurred header), so this
  // fixed overlay centers in the viewport instead of being clipped to the header.
  return (
    <motion.div className="fixed inset-0 z-[200] flex items-center justify-center overflow-y-auto p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <motion.div initial={{ scale: 0.94, y: 16, opacity: 0 }} animate={{ scale: 1, y: 0, opacity: 1 }} exit={{ scale: 0.96, opacity: 0 }}
        className="relative my-auto w-full max-w-[19rem] rounded-[2.2rem] bg-black p-5 pt-4 shadow-[0_30px_80px_rgba(0,0,0,0.7)]" data-testid="calc-modal">
        <div className="mb-1 flex justify-end">
          <button onClick={onClose} data-testid="calc-close" className="grid h-8 w-8 place-items-center rounded-full bg-white/10 text-white/70 active:bg-white/20"><X size={15} weight="bold" /></button>
        </div>
        <div className="mb-3 flex min-h-[72px] items-end justify-end px-2">
          <span className="truncate font-sans text-[58px] font-light leading-none text-white" data-testid="calc-display">{display}</span>
        </div>
        <div className="grid grid-cols-4 gap-3">
          <Key label="⌫" kind="util" onClick={back} testid="calc-back" />
          <Key label="AC" kind="util" onClick={clear} />
          <Key label="%" kind="util" onClick={pct} />
          <Key label="÷" kind="op" onClick={() => chooseOp("÷")} />
          <Key label="7" onClick={() => digit("7")} /><Key label="8" onClick={() => digit("8")} /><Key label="9" onClick={() => digit("9")} />
          <Key label="×" kind="op" onClick={() => chooseOp("×")} />
          <Key label="4" onClick={() => digit("4")} /><Key label="5" onClick={() => digit("5")} /><Key label="6" onClick={() => digit("6")} />
          <Key label="−" kind="op" onClick={() => chooseOp("−")} />
          <Key label="1" onClick={() => digit("1")} /><Key label="2" onClick={() => digit("2")} /><Key label="3" onClick={() => digit("3")} />
          <Key label="+" kind="op" onClick={() => chooseOp("+")} />
          <Key label="±" onClick={neg} /><Key label="0" onClick={() => digit("0")} /><Key label="." onClick={dot} />
          <Key label="=" kind="op" onClick={equals} />
        </div>
      </motion.div>
    </motion.div>
  );
}
